import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, X, Check, User, Sparkles } from 'lucide-react';

// Avatar sets grouped by theme -- 2026-07-19: was a single flat "aliens"
// list; added Robots and Astronauts as genuinely new theme categories
// (not just more aliens) so there's real variety to pick from. Every
// avatar is a real flat-vector SVG in the public/avatars/<theme>/ folder,
// selected the same way as before (picking a tile sets its /avatars/...
// path, saved via PUT /users/{id}/display-picture).
const AVATAR_THEMES = [
  {
    id: 'aliens',
    label: 'Aliens',
    avatars: [
      { id: 'alien_cap_and_shades', src: '/avatars/aliens/alien_cap_and_shades.svg', name: 'Cool Alien' },
      { id: 'alien_cap_money_shades', src: '/avatars/aliens/alien_cap_money_shades.svg', name: 'Rich Alien' },
      { id: 'alien_cool', src: '/avatars/aliens/alien_cool.svg', name: 'Cool Alien' },
      { id: 'alien_frown', src: '/avatars/aliens/alien_frown.svg', name: 'Sad Alien' },
      { id: 'alien_grumpy', src: '/avatars/aliens/alien_grumpy.svg', name: 'Grumpy Alien' },
      { id: 'alien_happy', src: '/avatars/aliens/alien_happy.svg', name: 'Happy Alien' },
      { id: 'alien_money_eyes_hat', src: '/avatars/aliens/alien_money_eyes_hat.svg', name: 'Money Eyes Alien' },
      { id: 'alien_sunglasses_smile', src: '/avatars/aliens/alien_sunglasses_smile.svg', name: 'Sunny Alien' },
      { id: 'alien_thinker', src: '/avatars/aliens/alien_thinker.svg', name: 'Thinking Alien' },
    ],
  },
  {
    id: 'robots',
    label: 'Robots',
    avatars: [
      { id: 'robot_cool', src: '/avatars/robots/robot_cool.svg', name: 'Cool Robot' },
      { id: 'robot_happy', src: '/avatars/robots/robot_happy.svg', name: 'Happy Robot' },
      { id: 'robot_grumpy', src: '/avatars/robots/robot_grumpy.svg', name: 'Grumpy Robot' },
      { id: 'robot_bullish', src: '/avatars/robots/robot_bullish.svg', name: 'Bullish Robot' },
      { id: 'robot_thinker', src: '/avatars/robots/robot_thinker.svg', name: 'Thinking Robot' },
    ],
  },
  {
    id: 'astronauts',
    label: 'Astronauts',
    avatars: [
      { id: 'astronaut_explorer', src: '/avatars/astronauts/astronaut_explorer.svg', name: 'Explorer' },
      { id: 'astronaut_happy', src: '/avatars/astronauts/astronaut_happy.svg', name: 'Happy Astronaut' },
      { id: 'astronaut_starstruck', src: '/avatars/astronauts/astronaut_starstruck.svg', name: 'Starstruck' },
      { id: 'astronaut_sleepy', src: '/avatars/astronauts/astronaut_sleepy.svg', name: 'Sleepy Astronaut' },
      { id: 'astronaut_moonwalker', src: '/avatars/astronauts/astronaut_moonwalker.svg', name: 'Moonwalker' },
    ],
  },
];

const AvatarSelectorModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  currentAvatar = null,
  loading = false
}) => {
  const { t } = useTranslation();
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [activeThemeId, setActiveThemeId] = useState(AVATAR_THEMES[0].id);

  // Reset selection when modal opens -- and jump to whichever theme tab
  // the current avatar (if any) actually belongs to, so re-opening the
  // picker doesn't strand the highlighted tile on a different tab.
  useEffect(() => {
    if (isOpen) {
      setSelectedAvatar(currentAvatar);
      const ownerTheme = AVATAR_THEMES.find((t) => t.avatars.some((a) => a.src === currentAvatar));
      setActiveThemeId(ownerTheme ? ownerTheme.id : AVATAR_THEMES[0].id);
    }
  }, [isOpen, currentAvatar]);

  const activeTheme = useMemo(
    () => AVATAR_THEMES.find((t) => t.id === activeThemeId) || AVATAR_THEMES[0],
    [activeThemeId]
  );

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle image load tracking
  const handleImageLoad = (avatarId) => {
    setLoadedImages(prev => new Set([...prev, avatarId]));
  };

  const handleSelect = () => {
    if (selectedAvatar && onSelect) {
      onSelect(selectedAvatar);
    }
  };

  const handleAvatarClick = (avatar) => {
    setSelectedAvatar(avatar.src);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn"
      onClick={handleBackdropClick}
    >
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-gray-700/50 animate-slideUp">
        
        {/* Header */}
        <div className="relative p-4 sm:p-6 border-b border-gray-700/50 bg-gradient-to-r from-blue-600/10 to-purple-600/10">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.1),transparent_50%)]" />
          
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2.5 bg-blue-600/20 rounded-xl backdrop-blur-sm border border-blue-500/20 shrink-0">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-white mb-0.5 truncate">{t('profileWidgets.avatarSelector.title')}</h2>
                <p className="text-xs sm:text-sm text-gray-400 truncate">{t('profileWidgets.avatarSelector.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700/50 rounded-xl transition-colors shrink-0 ml-2"
              aria-label={t('profileWidgets.avatarSelector.closeAria')}
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 hover:text-white transition-colors" />
            </button>
          </div>
        </div>

        {/* Theme tabs */}
        <div className="flex items-center gap-2 px-4 sm:px-6 pt-4 sm:pt-5 flex-wrap">
          {AVATAR_THEMES.map((theme) => (
            <button
              key={theme.id}
              onClick={() => setActiveThemeId(theme.id)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                activeThemeId === theme.id
                  ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-500/25'
                  : 'bg-gray-700/40 text-gray-300 hover:bg-gray-700/70 hover:text-white'
              }`}
            >
              {t(`profileWidgets.avatarSelector.themes.${theme.id}.label`, { defaultValue: theme.label })}
            </button>
          ))}
        </div>

        {/* Avatar Grid - Scrollable */}
        <div className="p-4 sm:p-6 overflow-y-auto overscroll-contain max-h-[calc(90vh-240px)] custom-scrollbar">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
            {activeTheme.avatars.map((avatar) => {
              const isSelected = selectedAvatar === avatar.src;
              const isLoaded = loadedImages.has(avatar.id);
              
              return (
                <button
                  key={avatar.id}
                  onClick={() => handleAvatarClick(avatar)}
                  className={`
                    relative aspect-square rounded-2xl border-2 transition-all duration-300 overflow-hidden
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900
                    ${isSelected 
                      ? 'border-blue-500 bg-blue-500/20 ring-4 ring-blue-500/30 shadow-lg shadow-blue-500/25 scale-[0.98]' 
                      : 'border-gray-600/50 hover:border-gray-500/70 bg-gray-700/30 hover:bg-gray-700/50 hover:scale-105 active:scale-95'
                    }
                  `}
                  aria-label={t('profileWidgets.avatarSelector.selectAria', { name: t(`profileWidgets.avatarSelector.themes.${activeTheme.id}.avatars.${avatar.id}`, { defaultValue: avatar.name }) })}
                  aria-pressed={isSelected}
                >
                  {/* Loading placeholder */}
                  {!isLoaded && (
                    <div className="absolute inset-0 bg-gray-700 animate-pulse flex items-center justify-center">
                      <User className="w-8 h-8 sm:w-10 sm:h-10 text-gray-500" />
                    </div>
                  )}

                  {/* Avatar image */}
                  <img
                    src={avatar.src}
                    alt={t(`profileWidgets.avatarSelector.themes.${activeTheme.id}.avatars.${avatar.id}`, { defaultValue: avatar.name })}
                    className={`
                      w-full h-full object-contain p-2 sm:p-3 transition-opacity duration-300
                      ${isLoaded ? 'opacity-100' : 'opacity-0'}
                    `}
                    onLoad={() => handleImageLoad(avatar.id)}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      console.warn(`Failed to load avatar: ${avatar.src}`);
                    }}
                  />

                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute top-2 right-2 animate-scaleIn">
                      <div className="bg-blue-500 rounded-full p-1 shadow-lg">
                        <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                      </div>
                    </div>
                  )}

                  {/* Hover overlay with name */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-2">
                    <div className="bg-black/70 backdrop-blur-sm px-2 py-1 rounded-lg">
                      <span className="text-xs sm:text-sm text-white font-medium">
                        {t(`profileWidgets.avatarSelector.themes.${activeTheme.id}.avatars.${avatar.id}`, { defaultValue: avatar.name })}
                      </span>
                    </div>
                  </div>

                  {/* Selected overlay */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-blue-500/10 pointer-events-none" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-gray-700/50 bg-gray-800/50 backdrop-blur-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              {selectedAvatar ? (
                <>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  <p className="text-sm text-gray-300 font-medium">{t('profileWidgets.avatarSelector.avatarSelected')}</p>
                </>
              ) : (
                <p className="text-sm text-gray-400">{t('profileWidgets.avatarSelector.chooseToContinue')}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 sm:flex-none px-6 py-2.5 text-gray-300 hover:text-white hover:bg-gray-700/50 rounded-xl transition-all font-medium"
              >
                {t('profileWidgets.avatarSelector.cancel')}
              </button>
              <button
                onClick={handleSelect}
                disabled={!selectedAvatar || loading}
                className={`
                  flex-1 sm:flex-none px-6 py-2.5 rounded-xl font-medium transition-all duration-200 shadow-lg
                  ${selectedAvatar && !loading
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-500 hover:to-blue-600 hover:shadow-blue-500/25 active:scale-95'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed opacity-50'
                  }
                `}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>{t('profileWidgets.avatarSelector.setting')}</span>
                  </div>
                ) : (
                  t('profileWidgets.avatarSelector.setAvatar')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes scaleIn {
          from { 
            transform: scale(0);
            opacity: 0;
          }
          to { 
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(31, 41, 55, 0.5);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(75, 85, 99, 0.8);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(107, 114, 128, 1);
        }
      `}</style>
    </div>
  );
};

export default AvatarSelectorModal;